import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  doCreateUserWithEmailAndPassword,
  doSendEmailVerification,
  doUpdateProfile,
  doCheckUsernameUnique,
  doSetUserData,
} from "../firebase/auth";
import { set, ref } from "firebase/database"; // Importing directly here
import { database } from "../firebase/auth";
const Registration = () => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isRegistering, setIsRegistering] = useState(false);

  const navigate = useNavigate();

  // Validation functions
  const validatePassword = (password) =>
    password.length >= 6 && password.length <= 10;
  const validateEmail = (email) => /\S+@\S+\.\S+/.test(email);
  const validateUsername = (username) => /^[a-zA-Z0-9_]{3,20}$/.test(username);

  const handleRegistration = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    // Validation checks before proceeding
    if (!username || !email || !password || !confirmPassword) {
      setErrorMessage("All fields are required.");
      return;
    }

    if (!validateUsername(username)) {
      setErrorMessage(
        "Username must be 3-20 characters, containing only letters, numbers, and underscores."
      );
      return;
    }

    if (!validateEmail(email)) {
      setErrorMessage("Please enter a valid email.");
      return;
    }

    if (!validatePassword(password)) {
      setErrorMessage("Password must be between 6 and 10 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    setIsRegistering(true);

    try {
      // Step 1: Check if username is available
      const isUsernameAvailable = await doCheckUsernameUnique(username);
      if (!isUsernameAvailable) {
        throw new Error("auth/username-in-use");
      }

      // Step 2: Create user account
      const userCredential = await doCreateUserWithEmailAndPassword(
        email,
        password
      );
      const user = userCredential.user;

      // Step 3: Store username in `usernames` node for uniqueness check
      await set(ref(database, `usernames/${username}`), user.uid);

      // Step 4: Store user details under `/users/{uid}`
      await doSetUserData(user.uid, {
        username,
        email,
        balance: 0,
        plans: [],
        deposits: [],
        profits: [],
        withdrawals: [],
        createdAt: new Date().toISOString(),
      });

      // Step 5: Update profile & send email verification
      await doUpdateProfile(user, { displayName: username });
      await doSendEmailVerification(user);

      setSuccessMessage(
        "Registration successful! Please check your email to verify your account."
      );
      setTimeout(() => navigate("/login"), 3000);
    } catch (error) {
      const errorCode = error.code || error.message;
      const friendlyErrors = {
        "auth/email-already-in-use": "This email is already registered.",
        "auth/username-in-use": "Username is already taken.",
        "auth/invalid-email": "Please enter a valid email.",
        "auth/weak-password": "Password is too weak.",
        "auth/network-request-failed": "Network error, please try again.",
      };
      setErrorMessage(friendlyErrors[errorCode] || "Registration failed.");
    } finally {
      setIsRegistering(false);
    }
  };

  return (
    <div className="hero bg-base-200 min-h-screen backdrop-blur-sm">
      <div className="hero-content flex-col lg:flex-row-reverse">
        <div className="card bg-base-100 w-full max-w-sm shrink-0 shadow-2xl">
          <form className="card-body" onSubmit={handleRegistration}>
            <div className="form-control">
              <label className="label">
                <span className="label-text">Username</span>
              </label>
              <input
                type="text"
                placeholder="username"
                className="input input-bordered"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Email</span>
              </label>
              <input
                type="email"
                placeholder="email"
                className="input input-bordered"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Password</span>
              </label>
              <input
                type="password"
                placeholder="password"
                className="input input-bordered"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Confirm Password</span>
              </label>
              <input
                type="password"
                placeholder="confirm password"
                className="input input-bordered"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>

            {errorMessage && (
              <div className="text-red-500 text-sm mt-2">{errorMessage}</div>
            )}

            {successMessage && (
              <div className="text-green-500 text-sm mt-2">
                {successMessage}
              </div>
            )}

            <div className="form-control mt-6">
              <button
                type="submit"
                className={`btn bg-secondary-500 ${
                  isRegistering ? "loading" : ""
                }`}
                disabled={isRegistering}
              >
                Register
              </button>
            </div>
          </form>
          <div className="text-center mb-2">
            <p>
              Already have an account?{" "}
              <Link to="/login" className="text-secondary-500 underline">
                Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Registration;
